import {
  Component,
  Input,
  OnInit,
  ViewChild,
  ElementRef
} from '@angular/core';
import { OnyxEvent } from '../models/event';
import {
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import { Rsvp } from '../models/rsvp';
import { RsvpStore } from '../service/rsvp.store';
import { Observable } from 'rxjs/Observable';
import { isNullOrUndefined } from 'util';
import { CustomValidators } from 'ng2-validation';
import { PioneerValidators } from '../../shared/utils/pioneer.validators';
import { EnvironmentConfiguration } from '../../environment/services/environment.configuration';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';

@Component({
  selector: 'pioneer-add-rsvp',
  templateUrl: './add-rsvp.component.html'
})
export class AddRsvpComponent implements OnInit {

  rsvpForm: FormGroup;

  onyxEvent: OnyxEvent;

  fileExtensions: Array<string>;

  isUploadInProgress: boolean;

  @ViewChild('file') fileInput: ElementRef;

  @Input('event')
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  constructor(private formBuilder: FormBuilder,
              private rsvpStore: RsvpStore,
              private environment: EnvironmentConfiguration,
              private flashMessagesService: FlashMessagesService) {}

  ngOnInit() {

    this.isUploadInProgress = false;

    if (!this.onyxEvent) {
      throw new Error('The "event" attribute must be specified to use the AddRsvpComponent component');
    }

    this.rsvpForm = this.formBuilder.group({
      firstName: [ null, [ Validators.required, Validators.maxLength(255) ] ],
      lastName: [ null, [ Validators.required, Validators.maxLength(255) ] ],
      nights: [ null, [ Validators.required, CustomValidators.gte(1), PioneerValidators.isInteger ] ],
      roomRate: [ null, [ PioneerValidators.isValidCurrency ] ]
    });

    this.fileExtensions = this.environment.rsvps.acceptedFileExtensions;
  }

  get commissionRoomPercentage(): number {
    if (isNullOrUndefined(this.onyxEvent)) {
      return 0;
    }
    return this.onyxEvent.commissionRoomPercentage;
  }

  /**
   * Will show placeholder if null
   * @return revenue
   */
  get revenue(): number {
    const nights = this.rsvpForm.get('nights').value;
    const roomRate = this.rsvpForm.get('roomRate').value;

    // an empty form field comes in as null
    if (isNullOrUndefined(nights) || isNullOrUndefined(roomRate)) {
      return 0;
    }

    const calculation = Number(nights * roomRate);
    return Number(calculation.toFixed(2));
  }

  /**
   * Will show placeholder if null
   * @return commission
   */
  get commission(): number {
    const calculation = Number(this.revenue * this.commissionRoomPercentage / 100);
    return Number(calculation.toFixed(2));
  }

  addRsvp(): Observable<any> {

    this.rsvpForm.disable();

    const rsvp = new Rsvp({
      eventId: this.onyxEvent.eventId,
      firstName: this.rsvpForm.get('firstName').value,
      lastName: this.rsvpForm.get('lastName').value,
      nights: this.rsvpForm.get('nights').value,
      roomRate: this.rsvpForm.get('roomRate').value
    });

    let obs = this.rsvpStore.addRsvp(rsvp).finally(() => {
      // always re enable form
      this.rsvpForm.enable();
    });

    obs.subscribe(() => {
      this.resetForm();
    });

    return obs;
  }

  handleUploadError(errorMessage: string, logMessage: string = null) {
    this.isUploadInProgress = false;
    this.flashMessagesService.error(errorMessage, logMessage);
  }

  importRsvpViaCsv(inputEvent) {
    this.isUploadInProgress = true;

    if (!inputEvent || !inputEvent.srcElement || !inputEvent.srcElement.files || inputEvent.srcElement.files.size === 0) {
      this.handleUploadError('MESSAGES.ERROR.UPLOAD_RSVPS');
      return;
    }

    const file = inputEvent.srcElement.files[ 0 ];
    this.clearFileInput();

    if (this.environment.rsvps.acceptedFileTypes.indexOf(file.type) < 0) {
      this.handleUploadError('MESSAGES.ERROR.UPLOAD_RSVPS');
      return;
    }

    const eventId = this.onyxEvent.eventId;
    this.rsvpStore.importRsvpViaCsv(eventId, file)
      .finally(() => {
        this.isUploadInProgress = false;
      })
      .catch((error => {
        this.flashMessagesService.error('MESSAGES.ERROR.UPLOAD_RSVPS', `Error uploading RSVPs: ${error}`);
        return Observable.throw(error);
      }))
      .subscribe((response) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.RSVP_IMPORT', null, { rsvpCount: Number(response.text()) });
      });

  }

  /**
   * This needs to be called whenever we do an upload so that someone could do a second upload with a different file
   * without having to reload the page.
   */
  clearFileInput(): void {
    this.fileInput.nativeElement.value = null;
  }

  private resetForm(): void {
    this.rsvpForm.reset({
      firstName: null,
      lastName: null,
      nights: null,
      roomRate: null
    });
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/UAT-GroupPay/workspace/pioneer-web/src/app/event/add-rsvp/add-rsvp.component.ts